import {
  params,
  string as stringType,
  number as numberType,
  bool as boolType,
  arrayOf as arrayType,
  shape as shapeType,
  id as idType,
  customType,
} from '@thd-nucleus/data-sources';

const paginate = customType('V2PaginationInput').shape({
  pageNumber: stringType(),
  pageSize: stringType(),
});

export const name = shapeType({
  firstName: stringType(),
  lastName: stringType(),
});

export const address = shapeType({
  id: idType(),
  nickName: stringType(),
  name,
  postalAddress: shapeType({
    addressLine1: stringType(),
    addressLine2: stringType(),
    city: stringType(),
    stateProvince: stringType(),
    zipCode: stringType(),
    country: stringType(),
  }),
  isInstantCheckoutDefault: boolType(),
  phones: arrayType(
    shapeType({ number: stringType(), id: idType(), isPrimary: boolType() }),
  ),
  isDefault: boolType(),
  lastModifiedDate: numberType(),
});

export const addresses = params({
  userId: idType().isRequired(),
  customerAccountId: idType().isRequired(),
  channelId: numberType().isRequired(),
  paginate,
}).shape({
  addresses: arrayType(address),
});

export const payment = shapeType({
  billingAddress: address,
  id: idType(),
  isInstantCheckoutDefault: boolType(),
  type: stringType(),
  nickName: stringType(),
  isDefault: boolType(),
  cardDetails: shapeType({
    cardBrand: shapeType({
      name: stringType(),
      displayName: stringType(),
    }),
    cardHolderName: stringType(),
    cardNumberLast4: stringType(),
    expirationMonth: stringType(),
    expirationYear: stringType(),
    isExpired: boolType(),
  }),
});

export const paymentCards = params({
  userId: idType().isRequired(),
  customerAccountId: idType().isRequired(),
  channelId: numberType().isRequired(),
}).shape({ paymentCards: arrayType(payment) });

const profile = shapeType({
  customerAccountId: idType(),
  userProfile: shapeType({
    isEnrolledInInstantCheckout: boolType(),
    lastModifiedDate: numberType(),
    postalAddress: shapeType({ zipCode: stringType() }),
    name,
  }),
  lastModifiedDate: numberType(),
});

const profileInput = customType('V2B2cAccountProfileInput')
  .shape({
    customerAccountId: idType(),
    userProfile: shapeType({
      isEnrolledInInstantCheckout: boolType(),
      lastModifiedDate: numberType(),
      postalAddress: shapeType({ zipCode: stringType() }),
      name,
    }),
    lastModifiedDate: numberType(),
  })
  .isRequired();

const addressInput = customType('V2AddressInput')
  .shape({
    id: idType(),
    nickName: stringType(),
    name,
    postalAddress: shapeType({
      addressLine1: stringType(),
      addressLine2: stringType(),
      city: stringType(),
      stateProvince: stringType(),
      zipCode: stringType(),
      country: stringType(),
    }),
    isInstantCheckoutDefault: boolType(),
    phones: arrayType(
      shapeType({ number: stringType(), id: idType(), isPrimary: boolType() }),
    ),
    isDefault: boolType(),
    lastModifiedDate: numberType(),
  })
  .isRequired();

const addAddressInput = customType('V2AddressInput')
  .shape({
    name,
    postalAddress: shapeType({
      addressLine1: stringType(),
      addressLine2: stringType(),
      city: stringType(),
      stateProvince: stringType(),
      zipCode: stringType(),
      country: stringType(),
    }),
    isInstantCheckoutDefault: boolType(),
    phones: arrayType(
      shapeType({ number: stringType(), isPrimary: boolType() }),
    ),
    isDefault: boolType(),
  })
  .isRequired();

export const b2cProfile = params({
  userId: idType().isRequired(),
  customerAccountId: idType().isRequired(),
  channelId: numberType().isRequired(),
}).shape(profile);

export const orangePayToken = params({
  userId: idType().isRequired(),
  customerAccountId: idType().isRequired(),
  channelId: numberType().isRequired(),
}).shape({
  token: stringType(),
});

export const cityStateByZipCode = params({
  zipCode: stringType(),
}).arrayOf(
  shapeType({
    city: stringType(),
    stateProvince: stringType(),
  }),
);

export const addAddresses = params({
  userId: idType().isRequired(),
  customerAccountId: idType().isRequired(),
  channelId: numberType().isRequired(),
  addresses: arrayType(addAddressInput).isRequired(),
})
  .mutation()
  .shape({ addresses: arrayType(address) });

export const updateAddresses = params({
  userId: idType().isRequired(),
  customerAccountId: idType().isRequired(),
  channelId: numberType().isRequired(),
  addresses: arrayType(addressInput),
})
  .mutation()
  .shape({ addresses: arrayType(address) });

const paymentInputOrangePay = customType('V2PaymentCardInput')
  .shape({
    id: idType(),
    isInstantCheckoutDefault: boolType(),
    nickName: stringType(),
    forterToken: stringType(),
    isDefault: boolType(),
    type: stringType(),
    cardDetails: shapeType({
      cardHolderName: stringType(),
      cardNumberLast4: stringType(),
      expirationMonth: stringType(),
      expirationYear: stringType(),
      accountNumberXref: stringType(),
      creditTenderTypeId: stringType(),
      cardClass: stringType(),
      creditHostId: stringType(),
      issuedCountryCode: stringType(),
      paymentToken: stringType(),
      bin: stringType(),
      panSuffix: stringType(),
      isGSA: stringType(),
      isPrepaid: stringType(),
      cardBrand: shapeType({
        name: stringType(),
        displayName: stringType(),
        paymentMethodCode: stringType(),
        reportPaymentMethodCode: stringType(),
      }),
    }),
    billingAddress: addressInput,
    cardReference: shapeType({
      cardCaptureSessionID: stringType(),
      encryptionBlock: stringType(),
      cardSecurityIndicator: stringType(),
      encryptionType: stringType(),
      keyId: stringType(),
      encryptionBlockFields: stringType(),
    }),
  })
  .isRequired();

const addPaymentInputOrangePay = customType('V2PaymentCardInput')
  .shape({
    isInstantCheckoutDefault: boolType(),
    nickName: stringType(),
    forterToken: stringType(),
    isDefault: boolType(),
    type: stringType(),
    xrefCardNumber: stringType(),
    buyerId: stringType(),
    cardDetails: shapeType({
      cardHolderName: stringType(),
      expirationMonth: stringType(),
      expirationYear: stringType(),
      accountNumberXref: stringType(),
      creditTenderTypeId: stringType(),
      cardClass: stringType(),
      creditHostId: stringType(),
      issuedCountryCode: stringType(),
      paymentToken: stringType(),
      bin: stringType(),
      panSuffix: stringType(),
      isGSA: stringType(),
      isPrepaid: stringType(),
      cardBrand: shapeType({
        name: stringType(),
        paymentMethodCode: stringType(),
        reportPaymentMethodCode: stringType(),
      }),
    }),
    billingAddress: addressInput,
    cardReference: shapeType({
      cardCaptureSessionID: stringType(),
      encryptionBlock: stringType(),
      cardSecurityIndicator: stringType(),
      encryptionType: stringType(),
      keyId: stringType(),
      encryptionBlockFields: stringType(),
    }),
  })
  .isRequired();

export const updateInstantCheckoutSettings = params({
  userId: idType().isRequired(),
  customerAccountId: idType().isRequired(),
  channelId: numberType().isRequired(),
  address: addressInput,
  paymentCard: paymentInputOrangePay,
  account: profileInput,
})
  .mutation()
  .shape({ account: profile, shippingAddress: address, paymentCard: payment });

export const updateInstantCheckoutSettingsAdd = params({
  userId: idType().isRequired(),
  customerAccountId: idType().isRequired(),
  channelId: numberType().isRequired(),
  address: addressInput,
  paymentCard: addPaymentInputOrangePay,
  account: profileInput,
})
  .mutation()
  .shape({ account: profile, shippingAddress: address, paymentCard: payment });

export const updateB2cProfile = params({
  userId: idType().isRequired(),
  customerAccountId: idType().isRequired(),
  channelId: numberType().isRequired(),
  account: profileInput,
})
  .mutation()
  .shape(profile);
